import type { CheckResult } from './checks.ts';

/**
 * RULE DCC-DEG-001 / DCC-DEG-002 / DCC-DEG-003
 * Source: Rules for Survival p.15 and the Degrees of Success & Failure table.
 *
 *  - Meeting or beating the Difficulty is a Success (a tie is "Success by 0").
 *  - Natural 20 (die face only) is a Critical Hit.
 *  - Natural 1 (die face only) is a Critical Fail.
 *  - Critical results do NOT also trigger the "by 10 or more" tiers; the
 *    crawler benefits from the single highest degree only.
 */
export type Degree =
  | 'critical-hit'
  | 'amazing-success'
  | 'standard-success'
  | 'near-miss-fail'
  | 'standard-fail'
  | 'major-fail'
  | 'critical-fail';

export const DEGREE_LABELS: Record<Degree, string> = {
  'critical-hit': 'Critical Hit',
  'amazing-success': 'Amazing Success',
  'standard-success': 'Standard Success',
  'near-miss-fail': 'Near Miss Fail',
  'standard-fail': 'Standard Fail',
  'major-fail': 'Major Fail',
  'critical-fail': 'Critical Fail',
};

/**
 * The seven degrees, as data.
 *
 * DERIVED from `DEGREE_LABELS` so there is no second list to keep in step, but
 * through `Object.keys`, which returns only OWN enumerable keys. That is the
 * whole point of this constant existing.
 */
export const DEGREES: readonly Degree[] = Object.keys(DEGREE_LABELS) as Degree[];

/**
 * Whether a persisted value is exactly one of the seven Degrees.
 *
 * `value in DEGREE_LABELS` LOOKS like this and is not: `in` walks the prototype
 * chain, so `'toString'`, `'constructor'` and `'__proto__'` all answer true. A
 * persisted Evade result carrying `degree: 'constructor'` would therefore have
 * validated, reached `degreeIsSuccess`, been read as a failure, and shown a
 * crawler an outcome no die produced.
 *
 * The general shape, worth remembering: **`in` is a lookup, not a membership
 * test.** Validating against a Record of LABELS was the deeper mistake -
 * presentation is not an enum, and this is the authority instead.
 */
export function isDegree(value: unknown): value is Degree {
  return typeof value === 'string' && (DEGREES as readonly string[]).includes(value);
}

export interface Resolution {
  degree: Degree;
  success: boolean;
  difficulty: number;
  total: number;
  /** total - difficulty. Positive means the Check beat the Difficulty. */
  margin: number;
}

export function resolveAgainstDifficulty(check: CheckResult, difficulty: number): Resolution {
  const margin = check.total - difficulty;
  const base = { difficulty, total: check.total, margin };

  if (check.naturalTwenty) return { ...base, degree: 'critical-hit', success: true };
  if (check.naturalOne) return { ...base, degree: 'critical-fail', success: false };

  if (margin >= 10) return { ...base, degree: 'amazing-success', success: true };
  if (margin >= 0) return { ...base, degree: 'standard-success', success: true };
  if (margin >= -2) return { ...base, degree: 'near-miss-fail', success: false };
  if (margin >= -9) return { ...base, degree: 'standard-fail', success: false };
  return { ...base, degree: 'major-fail', success: false };
}


/**
 * RULE DCC-CHK-00x - which degrees are a Success.
 *
 * ONE list, so nothing derives it again. A copy of these three strings in a
 * panel, or a boolean persisted alongside the degree, is a second answer that
 * can disagree with this one - and the disagreement would surface as a crawler
 * being told they evaded an attack the mechanics counted as a hit.
 *
 * The failures matter individually as well as collectively: `critical-fail` is
 * a natural 1, which the source treats as far worse than an ordinary miss, so
 * the DEGREE is what gets persisted and this is only ever a reading of it.
 */
export function degreeIsSuccess(degree: Degree): boolean {
  return degree === 'critical-hit' || degree === 'amazing-success' || degree === 'standard-success';
}
